import styled, { css } from 'styled-components';
import { theme } from '../../../theme';
import { Link as RouterLink } from 'react-router-dom';

export const Heading1 = styled.h1`
    &.sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
    }
`;

/* Header */

export const Header = styled.header`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    text-align: center;
    padding: 0;
    height: 60px;
    background: white;
    z-index: 1000;

    &.transparent {
        background: transparent;
    }

    .logo {
        margin: 20px auto 0;

        width: 80px;

        ${props => props.isInStoreMode && css`
            width: 120px;
            height: 63px;
            padding-top: 20px;
        `}
    }
`;
  
  /* Page structure */
  
export const Main = styled.main`
    max-width: 1024px;
    margin: 0 auto;

    &.loadingScreen {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
    }
`;


export const TitleSection = styled.section`
    background: white;
    text-align: center;
    margin: 0.5em 0 0;

    h1 {
        margin: 0 0 1em;

        @media screen and (min-width: 768px) and (min-height: 600px) {
            font-size: 32px;
        }

        /* Larger font size for in-store kiosk use */
        ${props => props.isInStoreMode && css`
            @media screen and (min-width: 768px) {
                font-size: 42px;
            }
        `}
    }

    p {
        /* Larger font size for in-store kiosk use */
        ${props => props.isInStoreMode && css`
            @media screen and (min-width: 768px) {
                font-size: 24px;
            }
        `}
    }

    &.compact h1 {
        font-size: 20px;

        @media screen and (min-width: 768px) and (min-height: 600px) {
            font-size: 32px;
        }

        /* Larger font size for in-store kiosk use */
        ${props => props.isInStoreMode && css`
            @media screen and (min-width: 768px) {
                font-size: 38px;
            }
        `}
    }
`;

export const DescriptionSection = styled.section`
    margin-bottom: 2em;

    @media screen and (min-width: 768px) and (min-height: 600px) {
        font-size: 18px;
    }

    /* Larger font size for in-store kiosk use */
    ${props => props.isInStoreMode && css`
        @media screen and (min-width: 768px) {
            font-size: 22px;
            
            p {
                font-size: 22px;
                margin-bottom: 1.2em;
            }
        }
    `}
`;

/* Global content styling for in-store mode */
export const InStoreContentWrapper = styled.div`
    /* Apply larger font sizes to common text elements when in in-store mode */
    ${props => props.isInStoreMode && css`
        @media screen and (min-width: 768px) {
            /* Product names and headings */
            h2, h3 {
                font-size: 24px !important;
                
                .brand {
                    font-size: 20px !important;
                }
                
                .product {
                    font-size: 26px !important;
                }
            }
            
            /* General text */
            p, div, span, li {
                font-size: 18px;
            }
            
            /* Product details */
            .details, .product-details {
                font-size: 16px !important;
            }
            
            /* Bundle list product names */
            .product-name {
                font-size: 22px !important;
            }
            
            /* No products message */
            .no-products-message p {
                font-size: 20px;
            }
        }
    `}
`;

/* Components */

export const ButtonStyling = css`
    appearance: none;
    background: none;
    color: inherit;
    border: none;
    font: inherit;
    cursor: pointer;
    outline: inherit;
    display: block;
    padding: 7px 20px;
    border-radius: 4px;
    font-size: 18px;

    /* Larger buttons for in-store kiosk use */
    ${props => props.isInStoreMode && css`
        padding: 16px 32px;
        border-radius: 8px;
        font-size: 24px;
        font-weight: 600;
        min-height: 56px;
    `}

    &.primary {
        background: ${theme.bootsBlue};
        color: white;
    }

    &.secondary {
        border: 1px ${theme.bootsBlue} solid;
        color: ${theme.bootsBlue};
        
        /* Thicker border for in-store mode */
        ${props => props.isInStoreMode && css`
            border-width: 2px;
        `}
    }

    &.small {
        font-size: 14px;
        height: 34px;
        padding: 5px 20px;
        margin: 0 auto;
        
        /* Override small size for in-store mode */
        ${props => props.isInStoreMode && css`
            font-size: 18px;
            height: 48px;
            padding: 12px 24px;
        `}
    }

    &.plain-link {
        display: inline;
        padding: 0;
        height: auto;
        border-radius: 0;
        font-size: inherit;
        font-weight: bold;
        text-decoration: underline;
    }

    &.skip-button {
        margin-top: 100px;
    }

    &[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
        background: transparent;
        border: 0 none;
        color: ${theme.bootsBlue};
    }
`;

export const Button = styled.button`
    ${ButtonStyling}
`;

export const Link = styled(RouterLink)`
    ${ButtonStyling}
    text-align: center;
    box-sizing: border-box;
    text-decoration: none;
`;

export const LinkButton = styled.a`
    text-align: center;
    box-sizing: border-box;
    padding-block: 11px;
    text-decoration: none;
`;

export const Footer = styled.footer`
    &.full {
        position: relative;
        max-width: 1024px;
        margin: 0 auto;
        padding: 2em 2em 4em;
    }

    &.full::before {
        content: "";
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 0;
        display: block;
        width: 100vw;
        bottom: 0;
        background: #E5E5E5;
        z-index: -1;
    }

    &.sticky {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        background: white;
        padding: 0;
        box-shadow: 0 -5px 10px rgba(0,0,0,0.03);
    }

    &.sticky-mobile-only {
        @media screen and (max-width: 767px) {
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
            background: white;
            padding: 0;
            box-shadow: 0 -5px 10px rgba(0,0,0,0.03);
        }
    }

    &.sticky.transparent {
        background: transparent;
        box-shadow: none;
    }

    .button-container {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 1em;
        align-items: center;
        width: 100%;
        padding: 1em 0;

        @media screen and (min-width: 768px) and (min-height: 600px) {
            max-width: 800px;
            margin: 0 auto;
            flex-direction: row-reverse;
            align-items: center;
            justify-content: center;
            gap: 1em;
            padding: 1em 0;
        }

        &.primary-last-mobile {
            flex-direction: column-reverse;

            @media screen and (min-width: 768px) and (min-height: 600px) {
                flex-direction: row-reverse;
            }
        }

        &.compact {
            gap: 0.5em;
            padding: 0.75em 0;

            .btn {
                height: 44px;
            }
        }
    }

    .btn {
        width: 90%;

        @media screen and (min-width: 480px) {
            max-width: 300px;
        }

        @media screen and (min-width: 768px) and (min-height: 600px) {
            margin-top: 0;
        }
    }
`;

export const ScreenReaderOnly = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
`;

export const Price = styled.span`
    &.offer-applied {
        .rrp {
            opacity: 0.5;
            text-decoration: line-through;
            color: rgba(0, 0, 0, 0.8);
            font-weight: normal;
            margin-right: 0.5em;
        }
        
        .offer {
            color: ${theme.redBackground};
            font-weight: bold;
            opacity: 1;
        }
    }
`;